import React from 'react';
import { useToolStore } from '../../store/useToolStore';
import { INTERIOR_THEMES } from '../../data/interiorThemes';
import { INTERIOR_PALETTES } from '../../data/interiorPalettes';
import { Sofa, Palette, Clock, Home, Sparkles } from 'lucide-react';
import { APP_VERSION } from '../../config/version';

const ROOM_TYPES = [
    { value: 'living_room', label: 'Living Room', icon: '🛋️' },
    { value: 'bedroom', label: 'Bedroom', icon: '🛏️' },
    { value: 'kitchen', label: 'Kitchen & Dining', icon: '🍳' },
    { value: 'office', label: 'Office / Study', icon: '💼' },
    { value: 'bathroom', label: 'Bathroom', icon: '🚿' },
];

const InteriorRightPanel: React.FC = () => {
    const { interiorParams, setInteriorParams } = useToolStore();
    const { theme, paletteId, timeOfDay, roomType } = interiorParams;

    return (
        <div className="2xl:w-[360px] xl:w-80 lg:w-72 w-80 bg-black border-l border-zinc-800 flex h-full shadow-2xl z-20 shrink-0 flex-col">
            {/* Header */}
            <div className="px-5 py-4 border-b border-zinc-800/60 shrink-0">
                <div className="flex items-center gap-2">
                    <Sofa size={14} className="text-violet-400" />
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Interior Controls</span>
                </div>
            </div>

            {/* Scrollable Controls */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 flex flex-col gap-6">

                {/* Room Type */}
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                        <Home size={12} />
                        <span>Room Type</span>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                        {ROOM_TYPES.map(room => (
                            <button
                                key={room.value}
                                onClick={() => setInteriorParams({ roomType: room.value })}
                                className={`px-3 py-2 rounded-xl border transition-all flex items-center gap-2 text-left ${roomType === room.value
                                    ? 'bg-violet-500/20 border-violet-500/50 text-white'
                                    : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10 hover:text-zinc-300'
                                    }`}
                            >
                                <span className="text-sm">{room.icon}</span>
                                <span className="text-xs font-medium">{room.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Design Theme */}
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                        <Sofa size={12} />
                        <span>Design Theme</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {INTERIOR_THEMES.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setInteriorParams({ theme: t.id })}
                                className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${theme === t.id
                                    ? 'bg-violet-500/20 border-violet-500/50 text-white'
                                    : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10'
                                    }`}
                            >
                                <span className="text-base">{t.icon}</span>
                                <span className="text-[9px] font-medium">{t.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Color Palette */}
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                        <Palette size={12} />
                        <span>Color Palette</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {INTERIOR_PALETTES.map(palette => (
                            <button
                                key={palette.id}
                                onClick={() => setInteriorParams({ paletteId: palette.id })}
                                className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1.5 ${paletteId === palette.id
                                    ? 'bg-violet-500/20 border-violet-500/50 text-white'
                                    : 'bg-white/5 border-white/5 text-zinc-500 hover:bg-white/10'
                                    }`}
                            >
                                <div className="flex gap-0.5">
                                    {palette.colors.slice(0, 3).map((c, i) => (
                                        <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
                                    ))}
                                </div>
                                <span className="text-[9px] font-medium leading-tight text-center">{palette.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Time of Day */}
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                        <Clock size={12} />
                        <span>Lighting</span>
                    </div>
                    <div className="flex items-center bg-black/40 p-0.5 rounded-lg border border-white/5">
                        {['Day', 'Sunset', 'Night'].map(time => (
                            <button
                                key={time}
                                onClick={() => setInteriorParams({ timeOfDay: time })}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${timeOfDay === time
                                    ? 'bg-violet-600 text-white shadow'
                                    : 'text-zinc-500 hover:text-zinc-300'
                                    }`}
                            >
                                {time}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-900 bg-black/95 shrink-0">
                <div className="flex items-center justify-between text-[10px] text-zinc-600">
                    <span>Interior Engine v{APP_VERSION}</span>
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                        <span className="text-violet-400 font-medium">Active</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InteriorRightPanel;
